import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getEnvironment } from "../getEnvironment";
import { CopyToClipboard, HideString, ShowString } from "../ProjectIcons";
import { CodeSampleSwitcher } from "../../routes/codeSamples";

// How a data provider connects to Gundi: the endpoint to POST to and the API
// key, with sample code. Shared by the flow map's account panels and the
// Connection page so both offer the same CONNECT section.
const HowToConnect = ({ kind, integrationType, apiKey, isAdmin }: {
    kind: string;
    integrationType: string;
    apiKey: string | undefined;
    isAdmin: boolean;
}) => {
    const { t } = useTranslation();
    const [keyVisible, setKeyVisible] = useState(false);
    const copyText = (text: string, label: string) =>
        navigator.clipboard.writeText(text)
            .then(() => toast.info(t("{{label}} copied to clipboard", { label })))
            .catch(() => toast.error(t("Failed to copy to clipboard")));
    const rawEnv = getEnvironment();
    const env = rawEnv === "local" ? "dev" : rawEnv;
    const endpoint = kind === "webhook"
        ? (env === "production" ? "https://hooks.gundiservice.org/webhooks" : `https://hooks.${env}.gundiservice.org/webhooks`)
        : (env === "production" ? "https://sensors.api.gundiservice.org/v2/observations/" : `https://sensors.api.${env}.gundiservice.org/v2/observations/`);
    return (
        <div className="flex flex-col gap-4">
            <div className="rounded border border-ui-field-outline bg-white p-4">
                <p className="text-sm text-ui-text-primary-text mb-4">
                    {kind === "webhook"
                        ? t("Configure your source system to POST to the Gundi webhook endpoint below. Include your API key and the integration-type header with every request.")
                        : t("Send data to the Gundi observations endpoint below, including your API key with every request.")}
                </p>
                <div className="rounded border border-ui-field-outline bg-unlisted-very-light-gray overflow-hidden">
                    <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-ui-divider-lines">
                        <div className="flex flex-col min-w-0">
                            <span className="text-xs text-ui-text-secondary-text mb-0.5">{t("Endpoint")}</span>
                            <span className="font-mono text-sm text-ui-text-primary-text truncate">{endpoint}</span>
                        </div>
                        <button type="button" onClick={() => copyText(endpoint, t("Endpoint"))}
                            className="text-ui-text-secondary-text hover:text-ui-text-primary-text flex-shrink-0" aria-label={t("Copy endpoint")}>
                            <CopyToClipboard />
                        </button>
                    </div>
                    {kind === "webhook" && (
                        <div className="grid grid-cols-2 border-b border-ui-divider-lines bg-unlisted-very-light-gray px-4 py-2">
                            <span className="text-xs text-ui-text-secondary-text">{t("Header")}</span>
                            <span className="text-xs text-ui-text-secondary-text">{t("Value")}</span>
                        </div>
                    )}
                    <div className={`${kind === "webhook" ? "grid grid-cols-2" : "flex items-center justify-between gap-4"} border-b border-ui-divider-lines`}>
                        {kind === "webhook" ? (
                            <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-ui-divider-lines">
                                <span className="font-mono text-sm text-ui-text-primary-text">apikey</span>
                                <button type="button" onClick={() => copyText("apikey", t("Header name"))}
                                    className="text-ui-text-secondary-text hover:text-ui-text-primary-text flex-shrink-0" aria-label={t("Copy header name")}>
                                    <CopyToClipboard />
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col min-w-0 px-4 py-3">
                                <span className="text-xs text-ui-text-secondary-text mb-0.5">{t("API key")}</span>
                                <span className="font-mono text-sm text-ui-text-primary-text truncate">
                                    {apiKey ? (keyVisible && isAdmin ? apiKey : "••••••••••••••••") : t("Loading…")}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-between gap-2 px-4 py-3">
                            {kind === "webhook" && (
                                <span className="font-mono text-sm text-ui-text-primary-text flex-1 truncate">
                                    {apiKey ? (keyVisible && isAdmin ? apiKey : "••••••••••••••••") : t("Loading…")}
                                </span>
                            )}
                            {/* Revealing and copying the key stays an admin action, as it
                                was on the provider tab this panel replaced. */}
                            {isAdmin && (
                                <span className="flex items-center gap-2 flex-shrink-0 ml-auto">
                                    <button type="button" onClick={() => setKeyVisible((v) => !v)}
                                        className="text-ui-text-secondary-text hover:text-ui-text-primary-text" aria-label={keyVisible ? t("Hide API key") : t("Show API key")}>
                                        {keyVisible ? <HideString /> : <ShowString />}
                                    </button>
                                    <button type="button" onClick={() => apiKey && copyText(apiKey, t("API key"))}
                                        className="text-ui-text-secondary-text hover:text-ui-text-primary-text" aria-label={t("Copy API key")}>
                                        <CopyToClipboard />
                                    </button>
                                </span>
                            )}
                        </div>
                    </div>
                    {kind === "webhook" && (
                        <div className="grid grid-cols-2">
                            <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-ui-divider-lines">
                                <span className="font-mono text-sm text-ui-text-primary-text">x-gundi-integration-type</span>
                                <button type="button" onClick={() => copyText("x-gundi-integration-type", t("Header name"))}
                                    className="text-ui-text-secondary-text hover:text-ui-text-primary-text flex-shrink-0" aria-label={t("Copy header name")}>
                                    <CopyToClipboard />
                                </button>
                            </div>
                            <div className="flex items-center justify-between gap-2 px-4 py-3">
                                <span className="font-mono text-sm text-ui-text-primary-text truncate">{integrationType}</span>
                                <button type="button" onClick={() => copyText(integrationType, t("Header value"))}
                                    className="text-ui-text-secondary-text hover:text-ui-text-primary-text flex-shrink-0" aria-label={t("Copy header value")}>
                                    <CopyToClipboard />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="rounded border border-ui-field-outline bg-white p-4">
                <p className="text-sm text-ui-text-primary-text mb-4">
                    {kind === "webhook"
                        ? t("Share this sample code with any developers integrating with this Route. Every request must include the apikey and x-gundi-integration-type headers, a timestamp preferably in ISO 8601 format, latitude and longitude, and a device or source ID.")
                        : t("Share this sample code and API key with any developers who need to push data to this Route. They can use it as a starting point to integrate their system directly.")}
                </p>
                <CodeSampleSwitcher
                    kind={kind === "webhook" ? "webhook" : "api-push"}
                    endpoint={endpoint}
                    apiKey={keyVisible && isAdmin ? (apiKey || null) : null}
                    integrationType={integrationType}
                    height={kind === "webhook" ? "280px" : "240px"}
                />
                <p className="text-sm text-ui-text-primary-text mt-4">
                    <a href={kind === "webhook"
                        ? "https://support.earthranger.com/developer_docs/custom-webhook"
                        : "https://support.earthranger.com/developer_docs/gundi-api"}
                        target="_blank" rel="noreferrer" className="text-brand-accent-green underline">
                        {kind === "webhook" ? t("Learn more about Gundi's webhook API.") : t("Learn more about Gundi's API.")}
                    </a>
                </p>
            </div>
        </div>
    );
};

export default HowToConnect;
